import { useTranslation } from 'react-i18next';
import FormField from '@/components/FormField/FormField';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import { Notepad2 } from 'iconsax-react';
import { useFormContext } from 'react-hook-form';
import ProductSpecs from './ProductSpecs';

const SpecificationsForm = () => {
  const { t: t } = useTranslation();
  const { watch } = useFormContext();

  const length = watch('length');
  const width = watch('width');
  const height = watch('height');

  const cbm = (length && width && height) ? ((Number(length) * Number(width) * Number(height)) / 1000000) : 0;
  const cbmText = (length && width && height) ? `CBM (m³) = ${cbm}` : t('cbmFormula');

  return (
    <div
      id="specifications"
      className="bg-white dark:bg-dark-surface-primary p-4 md:p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border shadow-sm mb-8 animate-fade-in"
      data-testid="specifications-form"
    >
      <div className="mb-6 flex gap-4 items-center" data-testid="specifications-header">
        <div
          className="p-3 bg-soft-background dark:bg-dark-button-bg-secondary rounded-xl text-neutral-900 dark:text-dark-text-primary flex items-center justify-center"
          data-testid="specifications-icon-container"
        >
          <Notepad2 variant="Bold" size={24} />
        </div>
        <div data-testid="specifications-header-text">
          <TitleText
            text={t('productSpecifications')}
            size="lg"
            className="!font-bold !text-[18px] text-[#090909] dark:text-dark-text-primary !leading-[100%] !tracking-[0px]"
          />
          <InfoText
            text={t('logisticsDataDesc')}
            size="xs"
            className="!font-normal !text-[12px] text-[#777777] dark:text-dark-text-secondary !leading-[100%] !tracking-[0px] mt-1"
          />
        </div>
      </div>

      <div className="mb-8" data-testid="product-specs-container">
        <ProductSpecs />
      </div>

      <div data-testid="carton-details-container">
        <div className="mb-4" data-testid="carton-details-header">
          <TitleText
            text={t('cartonDetails')}
            size="base"
            className="!font-bold text-light-text-primary dark:text-dark-text-primary"
          />
        </div>

        <div className="flex flex-col gap-6" data-testid="carton-details-grid">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              name="itemPerCarton"
              id="itemPerCarton"
              type="number"
              className="!font-semibold text-neutral-900 dark:text-dark-text-primary"
              label={t('itemPerCarton')}
              placeholder={t('itemPerCartonPlaceholder')}
              data-testid="item-per-carton-field"
            />
            <FormField
              name="grossWeight"
              id="grossWeight"
              type="number"
              labelClassName="!font-semibold"
              label={t('grossWeightGW')}
              placeholder={t('grossWeightPlaceholder')}
              data-testid="gross-weight-field"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              name="length"
              id="length"
              type="number"
              labelClassName="!font-semibold"
              label={t('lengthLabel')}
              placeholder={t('lengthPlaceholder')}
              data-testid="length-field"
            />
            <FormField
              name="width"
              id="width"
              type="number"
              labelClassName="!font-semibold"
              label={t('widthLabel')}
              placeholder={t('widthPlaceholder')}
              data-testid="width-field"
            />
            <FormField
              name="height"
              id="height"
              type="number"
              labelClassName="!font-semibold"
              label={t('heightLabel')}
              placeholder={t('heightPlaceholder')}
              data-testid="height-field"
            />
          </div>

          <InfoText 
            text={cbmText}
            size="sm"
            className="!text-orange-400 !font-normal"
            data-testid="cbm-formula-text"
          />
        </div>
      </div>
    </div>
  );
};

export default SpecificationsForm;